"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.InMemoryEntitiesService = void 0;
const crypto_1 = require("crypto");
const constants_1 = require("./constants");
const missingEntityRedirect_1 = require("./missingEntityRedirect");
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
function defaultForDeclaration(decl) {
    if (decl.defaultValue !== undefined) {
        return decl.defaultValue;
    }
    switch (decl.type) {
        case 'number':
            return 0;
        case 'boolean':
            return false;
        case 'stack':
            return '[]';
        case 'string':
        default:
            return '';
    }
}
/**
 * In-memory entity store for Phase 1 tests and as the engine-facing contract
 * the backend will implement against the session DB in Phase 2.
 */
class InMemoryEntitiesService {
    constructor(entityTypes = []) {
        this.instances = new Map();
        this.variables = new Map();
        this.currentByPlayer = new Map();
        this.typeById = new Map(entityTypes.map(t => [t.id, t]));
    }
    setEntityTypes(entityTypes) {
        this.typeById.clear();
        for (const t of entityTypes) {
            this.typeById.set(t.id, t);
        }
        (0, missingEntityRedirect_1.warnUnknownEntityTypeIds)([...this.instances.values()], new Set(this.typeById.keys()), 'setEntityTypes');
    }
    /**
     * Mirror VariableStore default init: for each instance, add any variable declared on its
     * current type that is missing, using the type default. Existing values are left unchanged;
     * variables no longer in the type definition are not removed.
     *
     * @returns Newly filled bindings (for persistence callers).
     */
    ensureDeclaredVariableDefaults() {
        const added = [];
        for (const inst of this.instances.values()) {
            const typeDef = this.typeById.get(inst.typeId);
            if (!typeDef) {
                continue;
            }
            let map = this.variables.get(inst.uuid);
            if (!map) {
                map = new Map();
                this.variables.set(inst.uuid, map);
            }
            for (const decl of typeDef.variables) {
                if (map.has(decl.name)) {
                    continue;
                }
                const value = defaultForDeclaration(decl);
                map.set(decl.name, {
                    name: decl.name,
                    type: decl.type,
                    value
                });
                added.push({
                    entityId: inst.uuid,
                    name: decl.name,
                    value,
                    type: decl.type
                });
            }
        }
        return added;
    }
    createEntity(options) {
        const typeId = String(options.typeId ?? '').trim();
        if (!typeId) {
            throw new Error('createEntity failed: typeId is required (e.g. createEntity("archive")).');
        }
        const typeDef = this.typeById.get(typeId);
        if (!typeDef) {
            throw new Error(`createEntity failed: unknown entity type '${typeId}'. Declare it under ## Entity Types.`);
        }
        if (this.instances.size >= constants_1.MAX_ENTITIES_PER_SESSION) {
            throw new Error(`createEntity failed: session limit of ${constants_1.MAX_ENTITIES_PER_SESSION} entities reached.`);
        }
        let parentEntityId;
        const parentRaw = options.parentEntityId !== undefined ? String(options.parentEntityId).trim() : '';
        if (parentRaw) {
            const parent = this.instances.get(parentRaw);
            if (!parent) {
                throw new Error(`createEntity failed: parent entity '${parentRaw}' does not exist (check currentEntity() / parent uuid).`);
            }
            if (typeDef.allowedParentTypeIds.length > 0 && !typeDef.allowedParentTypeIds.includes(parent.typeId)) {
                throw new Error(`createEntity failed: entity type '${typeId}' does not allow parent type '${parent.typeId}'. ` +
                    `Allowed parent types: ${typeDef.allowedParentTypeIds.join(', ') || '(none)'}.`);
            }
            const parentType = this.typeById.get(parent.typeId);
            if (parentType &&
                parentType.allowedChildTypeIds.length > 0 &&
                !parentType.allowedChildTypeIds.includes(typeId)) {
                throw new Error(`createEntity failed: parent type '${parent.typeId}' does not allow child type '${typeId}'. ` +
                    `Allowed child types: ${parentType.allowedChildTypeIds.join(', ') || '(none)'}.`);
            }
            const childCount = [...this.instances.values()].filter(e => e.parentEntityId === parentRaw).length;
            if (childCount >= constants_1.MAX_CHILDREN_PER_PARENT) {
                throw new Error(`createEntity failed: parent '${parentRaw}' already has the maximum of ${constants_1.MAX_CHILDREN_PER_PARENT} children.`);
            }
            parentEntityId = parentRaw;
        }
        else if (typeDef.allowedParentTypeIds.length > 0) {
            throw new Error(`createEntity failed: entity type '${typeId}' requires a parent of type: ` +
                `${typeDef.allowedParentTypeIds.join(', ')}.`);
        }
        const requestedUuid = options.uuid !== undefined ? String(options.uuid).trim() : '';
        let uuid;
        if (requestedUuid) {
            if (!UUID_REGEX.test(requestedUuid)) {
                throw new Error(`createEntity failed: uuid '${requestedUuid}' is not a valid UUID.`);
            }
            if (this.instances.has(requestedUuid)) {
                throw new Error(`createEntity failed: entity '${requestedUuid}' already exists.`);
            }
            uuid = requestedUuid;
        }
        else {
            uuid = (0, crypto_1.randomUUID)();
        }
        const instance = {
            uuid,
            typeId,
            parentEntityId,
            createdAtMs: Date.now()
        };
        this.instances.set(uuid, instance);
        const varMap = new Map();
        for (const decl of typeDef.variables) {
            varMap.set(decl.name, {
                name: decl.name,
                type: decl.type,
                value: defaultForDeclaration(decl)
            });
        }
        this.variables.set(uuid, varMap);
        return uuid;
    }
    destroyEntity(uuid) {
        const id = String(uuid ?? '').trim();
        if (!id || !this.instances.has(id)) {
            return false;
        }
        const toDelete = this.collectSubtree(id);
        for (const d of toDelete) {
            this.instances.delete(d);
            this.variables.delete(d);
        }
        this.clearCurrentEntityForDestroyed(toDelete);
        return true;
    }
    getEntity(uuid) {
        return this.instances.get(String(uuid ?? '').trim());
    }
    listEntities(filter) {
        let list = [...this.instances.values()];
        if (filter?.typeId) {
            const t = filter.typeId.trim();
            list = list.filter(e => e.typeId === t);
        }
        if (filter && 'parentEntityId' in filter) {
            if (filter.parentEntityId === null) {
                list = list.filter(e => !e.parentEntityId);
            }
            else if (filter.parentEntityId !== undefined) {
                const p = String(filter.parentEntityId).trim();
                list = list.filter(e => e.parentEntityId === p);
            }
        }
        return list;
    }
    getEntityVariable(uuid, name) {
        return this.variables.get(String(uuid ?? '').trim())?.get(name);
    }
    setEntityVariable(uuid, name, value, type) {
        const id = String(uuid ?? '').trim();
        const map = this.variables.get(id);
        if (!map) {
            return false;
        }
        const existing = map.get(name);
        if (existing && existing.type !== type) {
            map.set(name, { name, type, value });
        }
        else {
            map.set(name, { name, type: existing?.type ?? type, value });
        }
        return true;
    }
    getCurrentEntityId(playerId) {
        return this.currentByPlayer.get(String(playerId ?? '').trim());
    }
    setCurrentEntity(playerId, uuid) {
        const id = String(uuid ?? '').trim();
        const pid = String(playerId ?? '').trim();
        if (!pid || !this.instances.has(id)) {
            return false;
        }
        this.currentByPlayer.set(pid, id);
        return true;
    }
    clearCurrentEntity(playerId) {
        this.currentByPlayer.delete(String(playerId ?? '').trim());
    }
    clearCurrentEntityForDestroyed(destroyedUuids) {
        for (const [playerId, cur] of [...this.currentByPlayer.entries()]) {
            if (destroyedUuids.has(cur)) {
                this.currentByPlayer.delete(playerId);
            }
        }
    }
    getDisplayName(uuid, typeDisplayNameFallback) {
        const id = String(uuid ?? '').trim();
        const inst = this.instances.get(id);
        if (!inst) {
            return '';
        }
        const raw = this.variables.get(id)?.get(constants_1.ENTITY_DISPLAY_NAME_VAR)?.value;
        const name = raw !== undefined && raw !== null ? String(raw).trim() : '';
        if (name) {
            return name;
        }
        const typeName = typeDisplayNameFallback ??
            this.typeById.get(inst.typeId)?.displayName ??
            inst.typeId;
        const short = id.length > 8 ? id.slice(0, 8) : id;
        return `${typeName} (${short})`;
    }
    getAllCurrentEntityIds() {
        return new Map(this.currentByPlayer);
    }
    replaceCurrentEntityIds(map) {
        this.currentByPlayer.clear();
        for (const [playerId, entityId] of map) {
            const pid = String(playerId ?? '').trim();
            const eid = String(entityId ?? '').trim();
            if (pid && eid && this.instances.has(eid)) {
                this.currentByPlayer.set(pid, eid);
            }
        }
    }
    replaceAll(instances, variables, currentByPlayer) {
        this.instances.clear();
        this.variables.clear();
        this.currentByPlayer.clear();
        for (const inst of instances) {
            this.instances.set(inst.uuid, { ...inst });
        }
        for (const [id, map] of variables) {
            this.variables.set(id, new Map(map));
        }
        if (currentByPlayer) {
            this.replaceCurrentEntityIds(currentByPlayer);
        }
        (0, missingEntityRedirect_1.warnUnknownEntityTypeIds)(instances, new Set(this.typeById.keys()), 'replaceAll/hydrate');
    }
    getAllVariableMaps() {
        const out = new Map();
        for (const [id, map] of this.variables) {
            out.set(id, new Map(map));
        }
        return out;
    }
    async flushPendingPersistence() {
        // In-memory only — nothing to flush.
    }
    collectSubtree(rootId) {
        const out = new Set([rootId]);
        let changed = true;
        while (changed) {
            changed = false;
            for (const inst of this.instances.values()) {
                if (inst.parentEntityId && out.has(inst.parentEntityId) && !out.has(inst.uuid)) {
                    out.add(inst.uuid);
                    changed = true;
                }
            }
        }
        return out;
    }
}
exports.InMemoryEntitiesService = InMemoryEntitiesService;
//# sourceMappingURL=EntitiesService.js.map